import React, { useState, useMemo } from "react";
import { colors } from "../shared/constants";
import { inputStyle, labelStyle } from "../shared/styles";
import { MonthGrid } from "../shared/MonthGrid";
import { formatDayLabel, parseDay } from "../shared/dateUtils";

// Choosing inspection days and cutting them into appointments is the same job
// whether a campaign is being created or an existing one is being extended, so
// both flows share this rather than keeping two copies in step.

export const INTERVALS = [5, 10, 15, 20, 30, 60];
export const DEFAULT_START = "09:00";
export const DEFAULT_END = "17:00";

// How many whole slots a window yields at a given interval.
export const slotsIn = (start, end, interval) => {
  if (!start || !end || end <= start) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return Math.floor((eh * 60 + em - (sh * 60 + sm)) / interval);
};

export function useSlotSetup() {
  const [selected, setSelected] = useState(() => new Set());
  const [times, setTimes] = useState({});
  const [slotMinutes, setSlotMinutes] = useState(15);
  const [capacity, setCapacity] = useState(1);

  const toggleDay = (dateStr) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
    setTimes((prev) =>
      prev[dateStr]
        ? prev
        : { ...prev, [dateStr]: { start: DEFAULT_START, end: DEFAULT_END } }
    );
  };

  // Days a person picked out of a month, not a range: a job can run Monday,
  // Tuesday and then Friday, and the days between are simply not worked.
  const days = useMemo(() => Array.from(selected).sort(), [selected]);

  const setTime = (date, key, value) =>
    setTimes((prev) => ({ ...prev, [date]: { ...prev[date], [key]: value } }));

  const applyToAll = () => {
    const first = times[days[0]];
    if (!first) return;
    setTimes((prev) => {
      const next = { ...prev };
      days.forEach((d) => (next[d] = { ...first }));
      return next;
    });
  };

  const total = useMemo(
    () =>
      days.reduce((sum, d) => {
        const t = times[d] || {};
        return sum + slotsIn(t.start, t.end, slotMinutes);
      }, 0),
    [days, times, slotMinutes]
  );

  // The first day with times that make no sense, or null when all are fine.
  const badDay = useMemo(
    () =>
      days.find((d) => {
        const t = times[d] || {};
        return !t.start || !t.end || t.end <= t.start;
      }) || null,
    [days, times]
  );

  const payloadDays = () =>
    days.map((d) => ({
      date: d,
      start_time: times[d].start,
      end_time: times[d].end,
    }));

  const reset = () => {
    setSelected(new Set());
    setTimes({});
  };

  return {
    selected,
    times,
    days,
    slotMinutes,
    setSlotMinutes,
    capacity,
    setCapacity,
    toggleDay,
    setTime,
    applyToAll,
    total,
    badDay,
    payloadDays,
    reset,
  };
}

export function DayPicker({ setup, cursor, onCursorChange, renderDay, hint }) {
  return (
    <>
      <MonthGrid
        year={cursor.year}
        month={cursor.month}
        onMonthChange={(year, month) => onCursorChange({ year, month })}
        selected={setup.selected}
        onDayClick={setup.toggleDay}
        renderDay={renderDay}
      />
      <div style={{ fontSize: "13px", color: colors.slate, marginTop: "12px" }}>
        {setup.days.length
          ? `${setup.days.length} day${setup.days.length !== 1 ? "s" : ""} selected`
          : hint || "Click each day the inspection will run."}
      </div>
    </>
  );
}

const rowStyle = {
  display: "grid",
  gridTemplateColumns: "1.3fr 110px 110px 70px",
  gap: "12px",
  alignItems: "center",
  padding: "8px 4px",
  borderBottom: `1px solid ${colors.border}`,
  fontSize: "14px",
};

export function TimesStep({ setup, extra }) {
  const { days, times, slotMinutes, setSlotMinutes, capacity, setCapacity } = setup;

  const pill = (active, onClick, label) => (
    <button
      key={label}
      onClick={onClick}
      style={{
        padding: "6px 14px",
        borderRadius: "20px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        border: `1px solid ${active ? colors.darkGrey : colors.border}`,
        backgroundColor: active ? colors.darkGrey : colors.white,
        color: active ? colors.white : colors.slate,
        fontSize: "12px",
        fontWeight: "600",
      }}
    >
      {label}
    </button>
  );

  return (
    <>
      <div style={rowStyle}>
        <div style={{ ...labelStyle, marginBottom: 0 }}>Day</div>
        <div style={{ ...labelStyle, marginBottom: 0 }}>Start</div>
        <div style={{ ...labelStyle, marginBottom: 0 }}>Finish</div>
        <div style={{ ...labelStyle, marginBottom: 0, textAlign: "right" }}>Slots</div>
      </div>
      {days.map((d) => {
        const t = times[d] || {};
        return (
          <div key={d} style={rowStyle}>
            <div style={{ color: colors.text }}>{formatDayLabel(d)}</div>
            <input
              type="time"
              style={inputStyle}
              value={t.start || ""}
              onChange={(e) => setup.setTime(d, "start", e.target.value)}
            />
            <input
              type="time"
              style={inputStyle}
              value={t.end || ""}
              onChange={(e) => setup.setTime(d, "end", e.target.value)}
            />
            <div style={{ color: colors.slate, textAlign: "right" }}>
              {slotsIn(t.start, t.end, slotMinutes)}
            </div>
          </div>
        );
      })}

      {days.length > 1 && (
        <button
          onClick={setup.applyToAll}
          style={{
            background: "none",
            border: "none",
            color: colors.activeBlue,
            cursor: "pointer",
            fontSize: "13px",
            padding: "8px 0",
          }}
        >
          Apply the first day's times to all
        </button>
      )}

      <div style={{ marginTop: "16px" }}>
        <label style={labelStyle}>Slot length</label>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {INTERVALS.map((m) =>
            pill(slotMinutes === m, () => setSlotMinutes(m), `${m} min`)
          )}
        </div>
      </div>

      <div
        style={{ display: "flex", alignItems: "flex-end", gap: "20px", marginTop: "16px" }}
      >
        <div style={{ width: "110px" }}>
          <label style={labelStyle}>Units per slot</label>
          <input
            type="number"
            min="1"
            style={inputStyle}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
          />
        </div>
        {extra}
      </div>

      <div
        style={{
          marginTop: "16px",
          fontSize: "14px",
          fontWeight: "600",
          color: colors.text,
        }}
      >
        Generates {setup.total} slot{setup.total !== 1 ? "s" : ""} across {days.length} day
        {days.length !== 1 ? "s" : ""}
      </div>
    </>
  );
}

// The month a picker should open on: the campaign's window if it has one,
// otherwise today.
export function startingCursor(windowStart) {
  const d = parseDay(windowStart) || new Date();
  return { year: d.getFullYear(), month: d.getMonth() };
}
